const TermsContent = () => {
  return (
    <div className="text-muted-foreground space-y-4">
      <h3 className="font-heading text-foreground text-base font-bold">1. Service Overview</h3>
      <p className="leading-relaxed">
        qwip provides on-demand access to private work pods located in public spaces. Our pods offer a quiet, private workspace for phone calls, video meetings, focused work, or anything else you need a private, soundproof space for. By booking a session, you agree to these terms.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">2. Pricing</h3>
      <p className="leading-relaxed">
        Pricing may vary depending on the pod location, date, and time of booking. The applicable rate and any offers, discounts, or trial periods will be clearly displayed when you make your booking for that specific pod.
      </p>
      <p className="leading-relaxed">
        Billing begins from when you receive your access code and ends when you close your session via the link sent to your email.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">3. Billing & Payment</h3>
      <p className="leading-relaxed">
        By starting a session, you authorise qwip to charge your saved payment method for the duration of your pod usage at the rate displayed at booking. Your card will be charged when your session ends.
      </p>
      <p className="leading-relaxed">
        <strong>Important:</strong> You must end your session using the link sent to your email. If you do not, charges will continue until you end the session manually, the maximum session length of 4 hours is reached, or the pod closes at 8pm - whichever occurs first.
      </p>
      <p className="leading-relaxed">
        If your payment fails at the end of a session, we will automatically attempt to collect the payment again. If payment cannot be collected after multiple attempts, we will contact you to discuss next steps.
      </p>
      <p className="leading-relaxed">
        If you wish to dispute any charges, please contact us immediately at <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a>.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">4. Eligibility</h3>
      <p className="leading-relaxed">
        You must be at least 16 years old to use qwip.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">5. Pod Usage</h3>
      <p className="leading-relaxed">
        Pods are intended for quiet, professional work including video calls, phone calls, and focused tasks. You agree to:
      </p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li>Keep noise to reasonable levels</li>
        <li>Not consume food inside the pod (drinks are permitted)</li>
        <li>Remove all belongings and rubbish when you leave</li>
        <li>Not smoke, vape, or use illegal substances</li>
        <li>Not damage or tamper with the pod or its equipment</li>
      </ul>
      <p className="leading-relaxed">
        We reserve the right to terminate your session immediately if you breach these terms or engage in behaviour that violates our policies. No refund will be provided for sessions terminated due to policy violations.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">6. Session Limits</h3>
      <p className="leading-relaxed">
        Maximum session length is 4 hours. Pods operate Monday to Saturday from 10am to 9pm, and Sunday from 12pm to 6pm. Sessions cannot extend beyond closing time.
      </p>
      <p className="leading-relaxed">
        Whilst the pod is designed to reduce external noise, it is not 100% soundproof. This is intentional to enable you to hear fire alarms or evacuation announcements. You must comply with all evacuation procedures and exit the pod immediately if required.
      </p>
      <p className="leading-relaxed">
        If the pod malfunctions during your session, please contact us as soon as possible at <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a>. We will pause billing or provide a refund for any complete outages or malfunctions during your session.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">7. Damage & Liability</h3>
      <p className="leading-relaxed">
        You are responsible for any damage caused to the pod during your session. We reserve the right to charge your payment method for repair or replacement costs.
      </p>
      <p className="leading-relaxed">
        qwip is not liable for loss or damage to your personal belongings, or for any loss arising from technical issues, connectivity problems, or service interruptions.
      </p>
      <p className="leading-relaxed">
        qwip maintains public liability insurance for the operation of our pods.
      </p>
      <p className="leading-relaxed">
        You use the pod at your own risk. qwip accepts no liability for any injury or health issues that may arise from or be exacerbated by pre-existing medical conditions while using the pod.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">8. Privacy & Data</h3>
      <p className="leading-relaxed">
        Your personal data is processed in accordance with UK GDPR and our Privacy Policy. Access codes are single-use and expire when your session ends.
      </p>
      <p className="leading-relaxed">
        The pod is located in an area covered by Westfield White City's CCTV. The interior of the pod is not recorded.
      </p>
      <p className="leading-relaxed">
        We retain your personal data for as long as necessary to provide our services and comply with legal obligations. For more information, please see our Privacy Policy.
      </p>
      <p className="leading-relaxed">
        We may disclose your personal data to law enforcement or other authorities where required by law or in response to a valid legal request.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">9. WiFi</h3>
      <p className="leading-relaxed">
        WiFi is provided for lawful purposes only. You agree not to use the network for illegal downloads, unauthorised access to systems, or any activity that violates applicable laws. qwip is not responsible for the security of data transmitted over the network.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">10. Cancellations & Refunds</h3>
      <p className="leading-relaxed">
        There is no minimum session length. You may end your session at any time and will only be charged for the time used.
      </p>
      <p className="leading-relaxed">
        For technical issues, please contact <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a> within 48 hours of your session.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">11. Changes to These Terms</h3>
      <p className="leading-relaxed">
        We may update these terms from time to time. Continued use of qwip following any changes constitutes acceptance of the revised terms.
      </p>
      
      <h3 className="font-heading text-foreground text-base font-bold">12. Contact</h3>
      <p className="leading-relaxed">
        For questions, billing queries, or complaints: <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a>
      </p>
      <p className="leading-relaxed">
        qwip is operated by QWIP LTD, registered in England and Wales. Registered address: 128 City Road, London, EC1V 2NX.
      </p>
    </div>
  );
};

export default TermsContent;
